import React, { useEffect, useState, useRef, useCallback } from 'react';
import FormBuilder from './FormBuilder';
import Flow from './Flow';

export default function FormEditor({ form: initialForm, onSave, onAttachFlow, flowOnly = false }) {
  const DRAFT_KEY = 'formBuilderDraft';

  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (initialForm && draft.id === initialForm.id) return draft;
        if (!initialForm && draft.id) return draft;
      }
    } catch (e) {}
    return null;
  };

  const getInitial = () => {
    if (initialForm) return initialForm;
    const draft = loadDraft();
    if (draft) return draft;
    return { id: String(Date.now()), name: 'Untitled Form', description: '', fields: [], flow: null };
  };

  const [form, setForm] = useState(getInitial);
  const [activeTab, setActiveTab] = useState(flowOnly ? 'flow' : 'form');
  const [hasDraft, setHasDraft] = useState(!!loadDraft());
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(JSON.stringify(getInitial()));

  const doSave = useCallback((formToSave) => {
    if (!formToSave.name || !formToSave.name.trim()) {
      setSaveStatus('idle');
      return;
    }
    let savedFlow = null;
    try {
      const raw = localStorage.getItem('flow');
      if (raw) savedFlow = JSON.parse(raw);
    } catch (e) {}
    const updatedForm = { ...formToSave, flow: savedFlow };
    const serialized = JSON.stringify(updatedForm);
    if (serialized === lastSavedRef.current) {
      setSaveStatus('saved');
      return;
    }
    setSaveStatus('saving');
    lastSavedRef.current = serialized;
    if (onSave) onSave(updatedForm);
    setTimeout(() => setSaveStatus('saved'), 400);
  }, [onSave]);

  const scheduleSave = useCallback((formToSave) => {
    setSaveStatus('unsaved');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => doSave(formToSave), 1500);
  }, [doSave]);

  const handleFormChange = useCallback((newForm) => {
    setForm(newForm);
    scheduleSave(newForm);
  }, [scheduleSave]);

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  };

  useEffect(() => {
    localStorage.setItem('formFields', JSON.stringify(form.fields || []));
    window.dispatchEvent(new Event('formFieldsUpdate'));
  }, [form.fields]);

  useEffect(() => {
    const syncFlow = () => {
      const raw = localStorage.getItem('flow');
      if (raw) {
        try {
          const flowData = JSON.parse(raw);
          setForm(prev => {
            if (JSON.stringify(prev.flow) !== JSON.stringify(flowData)) {
              const updated = { ...prev, flow: flowData };
              scheduleSave(updated);
              return updated;
            }
            return prev;
          });
        } catch (e) {}
      }
    };

    const interval = setInterval(syncFlow, 500);
    return () => clearInterval(interval);
  }, [scheduleSave]);

  useEffect(() => {
    if (initialForm) {
      setForm(initialForm);
      if (initialForm.flow) {
        localStorage.setItem('flow', JSON.stringify(initialForm.flow));
        setTimeout(() => window.dispatchEvent(new Event('flowReload')), 100);
      } else {
        localStorage.removeItem('flow');
        setTimeout(() => window.dispatchEvent(new Event('flowReload')), 100);
      }
    }
  }, [initialForm?.id]);

  const handleSave = () => {
    doSave(form);
  };

  const handleTabChange = (tab) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      doSave(form);
    }
    if (activeTab === 'flow' && tab === 'form') {
      let savedFlow = null;
      try {
        const raw = localStorage.getItem('flow');
        if (raw) savedFlow = JSON.parse(raw);
      } catch (e) {}
      setForm(prev => ({ ...prev, flow: savedFlow }));
    } else if (activeTab === 'form' && tab === 'flow') {
      if (form.flow) {
        localStorage.setItem('flow', JSON.stringify(form.flow));
        setTimeout(() => window.dispatchEvent(new Event('flowReload')), 50);
      }
    }
    setActiveTab(tab);
  };

  if (flowOnly) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Flow />
        </div>
      </div>
    );
  }

  return (
    <div className="form-editor-container">
      <div className="form-editor-tabs">
        <button 
          className={`tab-button ${activeTab === 'form' ? 'active' : ''}`}
          onClick={() => handleTabChange('form')}
        >
           Form Builder
        </button>
        <button 
          className={`tab-button ${activeTab === 'flow' ? 'active' : ''}`}
          onClick={() => handleTabChange('flow')}
        >
           Approval Flow
        </button>
      </div>

      <div className="form-editor-content">
        {activeTab === 'form' && (
          <FormBuilder 
            form={form} 
            onChange={handleFormChange}
            saveStatus={saveStatus}
          />
        )}

        {activeTab === 'flow' && (
          <div style={{ height: '70vh' }}>
            <div className="flow-instructions">
              <p>Design the approval workflow for this form. Add nodes, assign employees, and connect them to create your approval chain.</p>
            </div>
            <Flow />
          </div>
        )}
      </div>
    </div>
  );
}
